import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { generatePath } from 'react-router-dom'
import { driversRoutesMap } from '@/modules/drivers/routes-config'
import { BusinessTripModel } from '@/app/models/businessTripModel'
import { formatTripTimespan } from '@/app/common/dateHelper'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { RouteComponentProps, withRouter } from '@/app/navigation/withRouter'

interface BusinessTripRowProps extends RouteComponentProps {
  trip: BusinessTripModel
  companyId: string
  selectedDriverId: string
  selectedIds: number[]
  toggleId: (id: number, isActive?: boolean) => void
  isSelectedAny: boolean
  showAuditDates?: boolean
  onContextMenu?: (event: React.MouseEvent, trip: BusinessTripModel) => void
}

const BusinessTripRow: React.FC<BusinessTripRowProps> = ({
  trip,
  companyId,
  selectedDriverId,
  selectedIds,
  toggleId,
  history,
  isSelectedAny,
  showAuditDates = false,
  onContextMenu,
}) => {
  const tripId = typeof trip.id === 'string' ? parseInt(trip.id, 10) : trip.id
  const isChecked = selectedIds.includes(tripId)
  const [isHovered, setIsHovered] = React.useState(false)

  const formatNumber = (value: number): string => {
    const num = Number(value.toFixed(2))
    const formatted = num.toFixed(2).replace('.', ',')
    return formatted
  }

  const formatDate = (date: Moment): string => {
    if (!date || !moment.isMoment(date)) return '-'
    return date.format('DD.MM.YY HH:mm')
  }

  const rowClassName = `notches-border--right tw-w-fit tw-cursor-pointer tw-border-b tw-border-b-base-300 tw-bg-white hover:tw-bg-primary/10 ${
    isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''
  }`

  const handleDoubleClick = () => {
    if (!selectedDriverId) return
    // Dwuklik odpala najpierw onClick dwa razy (toggleId wl. -> wyl.), wiec bez tego
    // nawigowalibysmy z pustym zaznaczeniem. Wzorzec jak w DriverContractDetailsRow.
    if (!selectedIds.includes(tripId)) toggleId(tripId, true)

    const path = generatePath(driversRoutesMap.driverBusinessTripEdit, {
      companyId,
      driverId: selectedDriverId,
      id: String(tripId),
    })
    history.push(path)
  }

  return (
    <tr
      className={rowClassName}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => toggleId(tripId)}
      onDoubleClick={handleDoubleClick}
      onContextMenu={(event) => onContextMenu?.(event, trip)}
    >
      <TableCheckbox
        disabled={false}
        background
        checked={isChecked}
        onChange={() => toggleId(tripId)}
        isHovered={isHovered}
      />
      <TableTextRow>{trip.number || '-'}</TableTextRow>
      <TableTextRow>{trip.registrationNumber || '-'}</TableTextRow>
      <TableTextRow>{formatDate(trip.startDate)}</TableTextRow>
      <TableTextRow>{formatDate(trip.endDate)}</TableTextRow>
      <TableTextRow>
        {`${formatTripTimespan(trip.timeLengthHome)} / ${formatTripTimespan(
          trip.timeLengthAway,
        )}`}
      </TableTextRow>
      <TableTextRow>{formatNumber(trip.amount)}</TableTextRow>
      <TableTextRow>{formatNumber(trip.parts)}</TableTextRow>
      <TableTextRow>{formatNumber(trip.accommodations)}</TableTextRow>
      <TableTextRow>{formatNumber(trip.invoices)}</TableTextRow>
      <TableTextRow>{formatNumber(trip.loans)}</TableTextRow>
      <TableTextRow>
        {trip.isTripSettled ? 'Rozliczona' : 'Nierozliczona'}
      </TableTextRow>
      <TableTextRow>{trip.destinationCountry || '-'}</TableTextRow>
      {showAuditDates && (
        <>
          <TableTextRow>
            {trip.creationDate?.isValid()
              ? formatDate(trip.creationDate)
              : '-'}
          </TableTextRow>
          <TableTextRow>
            {trip.modificationDate?.isValid()
              ? formatDate(trip.modificationDate)
              : '-'}
          </TableTextRow>
        </>
      )}
    </tr>
  )
}

export default withRouter(BusinessTripRow)
